<template>
  <div
    :class="[
      'labs-collection-card-small',
      !isLockedContent && 'labs-collection-card-small--clickable',
    ]"
    @click="onCardClickHandler"
  >
    <div class="labs-collection-card-small__container">
      <YearPill
        v-if="lab.year"
        :year="lab.year"
        straighten-corner="top-left bottom-right"
        class="labs-collection-card-small__year"
      />
      <div class="labs-collection-card-small__title-wrapper">
        <div class="labs-collection-card-small__title-main">
          <div
            v-tooltip="{
              content: `${lab.name}`,
              placement: 'bottom-center',
              classes: ['labs-collection-card-small__title-tooltip'],
            }"
            class="labs-collection-card-small__title-string"
          >
            {{ lab.name }}
          </div>
          <div class="labs-collection-card-small__title-states">
            <RunningPill v-if="isLabRunning" :showLabel="false" />
            <LockIcon
              v-if="isLockedContent"
              v-tooltip="{
                content: `${lockedContentTooltipText}`,
                placement: 'right',
              }"
              class="labs-collection-card-small__lock-icon"
            />
          </div>
        </div>
        <div class="labs-collection-card-small__title-cve">
          {{ lab.cve }}
        </div>
      </div>
      <SeparatorBorder gradient />
      <div class="labs-collection-card-small__details">
        <LearningAreaLabel
          v-for="la in lab.learning_areas"
          :key="la.id"
          :learningArea="la.name"
          :showIcon="false"
          :dotIcon="true"
        />
        <SeparatorBorder vertical height="14px" />
        <Difficulty
          :difficulty="lab.difficulty"
          :bold="false"
          :alwaysVisible="true"
          hideIcon
          class="labs-collection-card-small__details-difficulty"
        />
        <SeparatorBorder vertical height="14px" />
        <div class="labs-collection-card-small__details-duration">
          {{ durationValue }}
        </div>
        <SeparatorBorder
          vertical
          height="14px"
          class="labs-collection-card-small__details-separator--hide-mobile"
        />
        <SeverityPill :severity="lab.severity" />
        <template v-if="completed">
          <SeparatorBorder vertical height="14px" />
          <div class="labs-collection-card-small__completed">
            <CheckIcon /> Completed
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import SeparatorBorder from '@/components/shared/SeparatorBorder.vue'
import Difficulty from '@/components/properties/Difficulty.vue'
import LearningAreaLabel from '@/components/properties/LearningAreaLabel.vue'
import RunningPill from '@/components/properties/RunningPill.vue'
import SeverityPill from '@/components/properties/SeverityPill.vue'
import YearPill from '@/components/properties/YearPill.vue'
import CheckIconSvg from '@/assets/images/lab-experience/check-green.svg'
import LockIcon from '@/assets/icons/fill/lock.svg'
import { duration } from '@/helpers'
import { collectionCommons } from '@/helpers/mixins'
import CONSTANTS from '@/constants'

export default {
  name: 'LabsCollectionCardSmall',
  components: {
    SeparatorBorder,
    Difficulty,
    LearningAreaLabel,
    SeverityPill,
    RunningPill,
    YearPill,
    CheckIcon: { ...CheckIconSvg, name: 'CheckIcon' },
    LockIcon: { ...LockIcon, name: 'LockIcon' },
  },
  mixins: [collectionCommons],
  props: {
    lab: {
      type: Object,
      required: true,
    },
    isLockedContent: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  computed: {
    ...mapState({
      runningLabSession: state => state.labs.runningLabSession,
    }),
    durationValue() {
      return duration
        .durationFromSecs(this.lab.duration_in_seconds, 'zeros')
        .replace('h', 'h ')
    },
    showTitleTooltip() {
      return this.lab.name.length > 60
    },
    isLabRunning() {
      return this.runningLabSession?.lab_id === this.lab.uuid
    },
    completed() {
      return this.lab.user_status === CONSTANTS.CONTENT_USER_STATUS.FINISHED
    },
    lockedContentTooltipText() {
      return this.isBusinessUser && !this.isAccOwnerOrTeamManager
        ? 'This lab is locked. Request access.'
        : 'This lab is locked. Get access.'
    },
  },
  methods: {
    async onCardClickHandler() {
      if (this.isLockedContent) {
        await this.getAccessToLockedSDLab(this.lab)
      } else {
        this.$router.push({
          name: 'lab-page',
          params: { labId: this.lab.uuid },
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.labs-collection-card-small {
  $this: &;
  transition: 1s all ease;
  text-align: left;
  border-radius: 10px;
  background: var(--black-12);
  color: var(--white);
  width: 100%;
  &:hover {
    background: var(--tundora-gray);
  }
  &--clickable {
    cursor: pointer;
  }
  &__container {
    width: 100%;
    background: var(--glass-mid-gray);
    border-radius: 10px;
    padding: 20px 15px;
    display: flex;
    flex-flow: column;
    gap: 10px;
    position: relative;
    height: 100%;
    &:hover {
      background: var(--glass-mid-gray-light);
    }
    @include mobile {
      padding: 30px 15px;
    }
    @include mobile {
      padding: 30px;
    }
  }
  &__title {
    &-main {
      display: flex;
      align-items: flex-end;
      gap: 5px;
      .running-pill {
        background: transparent;
      }
      #{$this}__lock-icon {
        display: flex;
        fill: var(--white);
        width: 20px;
      }
    }
    &-string {
      font-size: rem(16px);
      font-style: normal;
      font-weight: 500;
      line-height: rem(23px);

      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &-cve {
      color: var(--light-blue-gray);
      font-size: rem(14px);
      font-style: normal;
      font-weight: 500;
      line-height: rem(20px);
    }
    &-states {
      display: flex;
      align-self: baseline;
      .running-pill {
        padding: 0;
        position: relative;
        top: 5px;
      }
    }
  }
  &__details {
    display: inline-grid;
    grid-template-columns: repeat(5, auto);
    align-items: center;
    gap: 10px;
    @include mobile {
      grid-template-columns: repeat(9, auto);
      width: fit-content;
    }
    @include desktop {
      grid-template-columns: repeat(5, auto);
      width: fit-content;
    }
    &-duration,
    &-difficulty {
      font-size: rem(12px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(14px);
      font-family: var(--font-book);
    }
    &-separator {
      &--hide-mobile {
        display: none;
        @include mobile {
          display: block;
        }
        @include desktop {
          display: none;
        }
      }
    }
  }
  &__completed {
    color: var(--positive-light);
    font-size: rem(12px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(14px);
    display: flex;
    align-items: center;
    gap: 5px;
  }
  &__year {
    position: absolute;
    top: 0;
    right: 0;
  }
}
</style>
<style lang="scss">
.labs-collection-card-small__title-tooltip {
  max-width: 200px;
}
</style>
